/**
 * Cart page notices.
 *
 * Renders a status line above the cart when
 *   - a product was just added to the cart. Adding usually navigates away
 *     before this page exists, so the product is handed over through
 *     sessionStorage, written by the hook in snippets/global-script.liquid.
 *   - a line was removed on the cart page. That message carries an "Undo?"
 *     link which adds the line back.
 */

const CART_NOTICE_ADDED_KEY = 'cart-notice:added';

/* How long a stored "added" message stays valid. Long enough to survive the
   redirect, or a detour through the cart drawer, but short enough that opening
   the cart later in the session does not show a stale message. */
const CART_NOTICE_ADDED_TTL = 300000;

class CartNoticeComponent extends HTMLElement {
  pendingRemoval = null;
  unsubscribeCartUpdate = null;

  connectedCallback() {
    /* Capture phase: cart.js takes the row out of the DOM as soon as it hears
       these events, so the line has to be read before they reach it. */
    document.addEventListener('onLineItemRemove', this.captureLineRemoval, true);
    document.addEventListener('quantity-selector:update', this.captureQuantityRemoval, true);
    document.addEventListener('cart:updated', this.onCartUpdated);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    }

    this.showStoredAddedNotice();
  }

  disconnectedCallback() {
    document.removeEventListener('onLineItemRemove', this.captureLineRemoval, true);
    document.removeEventListener('quantity-selector:update', this.captureQuantityRemoval, true);
    document.removeEventListener('cart:updated', this.onCartUpdated);
    this.unsubscribeCartUpdate?.();
  }

  /* ------------------------------------------------------------------ */
  /* Removal                                                             */
  /* ------------------------------------------------------------------ */

  captureLineRemoval = (event) => {
    const line = event?.detail?.line;
    if (line) this.pendingRemoval = this.readLine(line);
  };

  captureQuantityRemoval = (event) => {
    const { quantity, cartLine } = event?.detail || {};
    if (quantity === 0 && cartLine) this.pendingRemoval = this.readLine(cartLine);
  };

  readLine(line) {
    const row = document.querySelector(`.cart-items__table-row[data-line="${line}"]`);
    if (!row) return null;

    const input = row.querySelector('.quantity__input');
    const properties = {};

    row.querySelectorAll('.customization-option').forEach((option) => {
      const name = option.querySelector('dt')?.textContent?.trim().replace(/:$/, '');
      const value = option.querySelector('dd')?.textContent?.trim();
      if (name && value) properties[name] = value;
    });

    return {
      title: row.querySelector('.cart-item__name .text')?.textContent?.trim() || '',
      id: input?.dataset.quantityVariantId || '',
      /* The value *attribute* still holds the quantity the line was rendered
         with, so an undo after someone typed 0 restores the original amount. */
      quantity: Number(input?.getAttribute('value')) || Number(input?.value) || 1,
      properties,
    };
  }

  onCartUpdated = () => {
    const removed = this.pendingRemoval;
    this.pendingRemoval = null;

    if (removed?.title) this.renderRemoved(removed);
  };

  /* ------------------------------------------------------------------ */
  /* Add                                                                 */
  /* ------------------------------------------------------------------ */

  /* Only the product form reports the item that was just added; every other
     publisher sends the whole cart, where the first line means nothing here. */
  onCartUpdate = (data) => {
    if (data?.source !== 'product-form-component') return;

    /* The form also stores the title for the next cart page load. It was shown
       live here, so drop it before it turns into a stale message. */
    try {
      sessionStorage.removeItem(CART_NOTICE_ADDED_KEY);
    } catch (error) {
      // Storage can be unavailable — nothing to clean up in that case.
    }

    const title = CartNoticeComponent.titleFromResponse(data?.cartData);
    if (title) this.renderAdded(title);
  };

  showStoredAddedNotice() {
    let stored = null;

    try {
      stored = JSON.parse(sessionStorage.getItem(CART_NOTICE_ADDED_KEY) || 'null');
      sessionStorage.removeItem(CART_NOTICE_ADDED_KEY);
    } catch (error) {
      return;
    }

    if (!stored) return;
    if (Date.now() - Number(stored.at || 0) > CART_NOTICE_ADDED_TTL) return;

    /* A plain form post records only the variant, because Shopify redirects
       here before the response can be read. The line is on this page, so the
       title comes off the row it rendered. */
    const title = stored.title || CartNoticeComponent.titleFromCart(stored.variantId);
    if (title) this.renderAdded(title);
  }

  static titleFromCart(variantId) {
    if (!variantId) return '';

    const input = document.querySelector(`.quantity__input[data-quantity-variant-id="${variantId}"]`);
    const row = input?.closest('.cart-items__table-row');

    return row?.querySelector('.cart-item__name .text')?.textContent?.trim() || '';
  }

  static titleFromResponse(response) {
    if (!response) return '';
    const item = Array.isArray(response.items) ? response.items[0] : response;
    return item?.product_title || item?.title || '';
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  renderAdded(title) {
    const notice = this.buildNotice(this.dataset.addedText, title);
    const url = this.dataset.continueUrl;
    const label = this.dataset.continueText;

    if (url && label) {
      const link = document.createElement('a');
      link.className = 'cart-notice__action button-secondary';
      link.href = url;
      link.textContent = label;
      notice.append(link);
    }

    this.show(notice);
  }

  renderRemoved(removed) {
    const notice = this.buildNotice(this.dataset.removedText, removed.title);
    const label = this.dataset.undoText;

    if (label && removed.id) {
      const text = notice.querySelector('.cart-notice__text');
      const undo = document.createElement('button');
      undo.type = 'button';
      undo.className = 'cart-notice__undo';
      undo.textContent = label;
      undo.addEventListener('click', () => this.undoRemoval(undo, removed), { once: true });
      text.append(' ', undo);
    }

    this.show(notice);
  }

  buildNotice(template, title) {
    const notice = document.createElement('div');
    notice.className = 'cart-notice';
    notice.setAttribute('role', 'status');

    const icon = document.createElement('span');
    icon.className = 'cart-notice__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML =
      '<svg viewBox="0 0 20 20" focusable="false">' +
      '<circle cx="10" cy="10" r="9" fill="currentColor"/>' +
      '<path d="m5.9 10.3 2.7 2.7 5.5-5.9" fill="none" stroke="#fff" stroke-width="1.7" ' +
      'stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    const text = document.createElement('span');
    text.className = 'cart-notice__text';
    text.append(CartNoticeComponent.interpolate(template, title));

    notice.append(icon, text);
    return notice;
  }

  /* Builds the message as nodes rather than markup so a product title can never
     be read as HTML. */
  static interpolate(template, title) {
    const fragment = document.createDocumentFragment();
    const parts = String(template || '[product]').split('[product]');

    parts.forEach((part, index) => {
      if (part) fragment.append(document.createTextNode(part));

      if (index < parts.length - 1) {
        const name = document.createElement('span');
        name.className = 'cart-notice__product';
        name.textContent = title;
        fragment.append(name);
      }
    });

    return fragment;
  }

  show(notice) {
    this.replaceChildren(notice);
    this.hidden = false;
  }

  /* ------------------------------------------------------------------ */
  /* Undo                                                                */
  /* ------------------------------------------------------------------ */

  async undoRemoval(button, removed) {
    button.disabled = true;

    const body = { id: removed.id, quantity: removed.quantity };
    if (Object.keys(removed.properties || {}).length) body.properties = removed.properties;

    try {
      const response = await fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Undo failed with status ${response.status}`);

      /* The global add hook just recorded this as a fresh add; putting a line
         back is not one, so the reload below must not announce it. */
      try {
        sessionStorage.removeItem(CART_NOTICE_ADDED_KEY);
      } catch (error) {
        // Storage can be unavailable — nothing to clean up in that case.
      }

      window.location.reload();
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-notice-component')) {
  customElements.define('cart-notice-component', CartNoticeComponent);
}
